import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import axios from 'axios';

import LockIcon from '@public/icons/lock.svg';
import CheckIcon from '@public/icons/check.svg';

import useDisclosure from 'hooks/useDisclosure';

import { NewsletterProps } from 'interfaces/cms/content';
import { Modal } from 'components/common/Modal';

function Newsletter({ bg }: NewsletterProps) {
  const { t } = useTranslation('common');

  const [isValidEmail, setIsValidEmail] = useState(false);
  const [email, setEmail] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}\.[\d]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;

    setEmail(emailValue);
    setIsValidEmail(emailRegex.test(emailValue));
  };

  const onSubmit = async () => {
    try {
      await axios.post('/api/subscriber', { email });
      onOpen();
      setEmail('');
      setIsValidEmail(false);
    } catch (error) {
      console.log('Submit Subscription Failed: ', error);
    }
  };

  return (
    <>
      <div
        className="relative bg-no-repeat bg-bottom max-w-full flex flex-col items-center"
        style={{
          backgroundColor: bg,
        }}
      >
        <div
          className={clsx(
            'max-w-[69.375rem] w-full mx-auto text-center',
            'px-[0.9375rem] pt-[1.875rem] pb-[7.5rem] md:mx-[3.75rem]'
          )}
        >
          <span className="text-primaryColor text-2xl font-bold">
            {t('newsletter_title1')}
          </span>
          <wbr />
          <span className="text-primaryColor text-2xl font-bold whitespace-nowrap">
            {t('newsletter_title2')}
          </span>

          <div className="border-b-[4px] border-grayColor w-[8.125rem] mx-auto my-[1.875rem]"></div>

          <div className="flex flex-col md:flex-row md:justify-center">
            <div className="flex flex-row items-center">
              <input
                type="email"
                autoComplete="off"
                value={email}
                onChange={onChangeEmail}
                placeholder={t('newsletter_email_placeholder')}
                className={clsx(
                  'bg-white w-full md:w-[18.75rem] h-[3.0625rem] pl-4 pr-[2.5rem] text-lg',
                  'border border-grayColor rounded-[0.625rem]',
                  'focus:outline-none focus:border-buttonPrimaryBg',
                  'focus:shadow-lg focus:shadow-[0_7px_15px_0_rgba(42, 49, 203, 0.1)]'
                )}
              />
              <div
                className={clsx(
                  '-ml-[2rem] mt-1',
                  isValidEmail ? 'visible' : 'invisible'
                )}
              >
                <CheckIcon width={20} height={20} alt="check icon" />
              </div>
            </div>

            <button
              className={clsx(
                'h-[3.0625rem] ml-0 md:ml-[1.5rem] rounded-[0.625rem] min-w-[11.75rem] px-4 uppercase font-bold',
                'mt-[0.625rem] md:mt-0',
                isValidEmail
                  ? 'bg-primaryColor hover:bg-buttonPrimaryBgHover cursor-pointer text-white'
                  : 'bg-accordionPanelBg text-placeholder cursor-not-allowed'
              )}
              disabled={!isValidEmail}
              onClick={onSubmit}
            >
              {t('newsletter_button')}
            </button>
          </div>

          <div className="flex flex-row justify-center mt-[0.625rem] items-center flex-wrap">
            <LockIcon
              className="fill-textColor"
              width="10"
              height="12"
              viewBox="0 0 10 12"
            />
            <Link
              href="/privacy-policy"
              prefetch={false}
              passHref
              legacyBehavior
            >
              <span className="underline text-buttonPrimaryBg hover:text-buttonPrimaryBgHover cursor-pointer text-sm">
                {t('newsletter_disclaimer1')}
              </span>
            </Link>
            <span className="text-textColor text-sm mx-1">
              {t('newsletter_disclaimer2')}
            </span>
            <Link
              href="/terms-and-conditions"
              prefetch={false}
              passHref
              legacyBehavior
            >
              <span className="underline text-buttonPrimaryBg hover:text-buttonPrimaryBgHover cursor-pointer text-sm">
                {t('newsletter_disclaimer3')}
              </span>
            </Link>
          </div>
        </div>
      </div>

      <Modal isOpen={isOpen} onClose={onClose} customClass="max-w-md">
        <Image
          src="/images/newsletter/newsletter-thankyou.png"
          width={750}
          height={460}
          alt="Thank you"
          loading="lazy"
        />
        <div className="text-center mt-[1.875rem] text-2xl font-bold text-textColor">
          <span>{t('newsletter_thankyou1')}</span>
          <span className="nowrap">{t('newsletter_thankyou2')}</span>
        </div>
      </Modal>
    </>
  );
}

export default Newsletter;
